// src/store/slices/user.slice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { userController } from '../../controllers/user.controller'

/* ---------------------------------------------------------- */
/* ------------------ Async Thunks -------------------------- */
/* ---------------------------------------------------------- */

// הרשמה
export const signup = createAsyncThunk(
  'user/signup',
  async (userCred, { rejectWithValue }) => {
    try {
      return await userController.signup(userCred)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// התחברות
export const login = createAsyncThunk(
  'user/login',
  async (credentials, { rejectWithValue }) => {
    try {
      return await userController.login(credentials)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// התחברות עם גוגל
export const loginWithGoogle = createAsyncThunk(
  'user/loginWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      return await userController.loginWithGoogle()
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// התנתקות
export const logout = createAsyncThunk(
  'user/logout',
  async (_, { rejectWithValue }) => {
    try {
      await userController.logout()
      return null
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// בדיקת טוקן קיים
export const validateBackendToken = createAsyncThunk(
  'user/validateBackendToken',
  async (_, { rejectWithValue }) => {
    try {
      const user = await userController.validateToken()
      return user
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// שליחת מייל אימות
export const sendVerificationEmail = createAsyncThunk(
  'user/sendVerificationEmail',
  async (_, { rejectWithValue }) => {
    try {
      return await userController.sendVerificationEmail()
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// איפוס סיסמה
export const resetPassword = createAsyncThunk(
  'user/resetPassword',
  async (email, { rejectWithValue }) => {
    try {
      return await userController.resetPassword(email)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// עדכון פרופיל
export const updateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async (updates, { rejectWithValue }) => {
    try {
      return await userController.updateUserProfile(updates)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// מחיקת חשבון
export const deleteUserAccount = createAsyncThunk(
  'user/deleteUserAccount',
  async (password, { rejectWithValue }) => {
    try {
      await userController.deleteUserAccount(password)
      return null
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// בדיקת אימות אימייל
export const isEmailVerified = createAsyncThunk(
  'user/isEmailVerified',
  async (_, { rejectWithValue }) => {
    try {
      return await userController.isEmailVerified()
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// טעינת משתמש לפי ID
export const fetchUserById = createAsyncThunk(
  'user/fetchUserById',
  async (userId, { rejectWithValue }) => {
    try {
      return await userController.loadUserById(userId)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// טעינת משתמש לפי username
export const fetchUserByUsername = createAsyncThunk(
  'user/fetchUserByUsername',
  async (username, { rejectWithValue }) => {
    try {
      return await userController.loadUserByUsername(username)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// טעינת רשימת משתמשים
export const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  async (filterBy = {}, { rejectWithValue }) => {
    try {
      return await userController.getUsers(filterBy)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// טעינת עורכים מובילים
export const fetchTopEditors = createAsyncThunk(
  'user/fetchTopEditors',
  async (_, { rejectWithValue }) => {
    try {
      return await userController.getTopEditors()
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

/* ---------------------------------------------------------- */
/* ---------------------- Slice ----------------------------- */
/* ---------------------------------------------------------- */

const initialState = {
  loggedInUser: null,
  users: [],
  topEditors: [],
  isLoading: false,
  isUserChecked: false,
  error: null,
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoggedInUser(state, action) {
      state.loggedInUser = action.payload
    },
    setUserChecked(state, action) {
      state.isUserChecked = action.payload
    },
    setEmailVerified(state, action) {
      if (state.loggedInUser) {
        state.loggedInUser.emailVerified = action.payload
      }
    },
    setUsers(state, action) {
      state.users = action.payload
    },
    addUser(state, action) {
      state.users.push(action.payload)
    },
    updateUser(state, action) {
      const idx = state.users.findIndex(
        (user) => user._id === action.payload._id
      )
      if (idx !== -1) state.users[idx] = action.payload
      if (state.loggedInUser?._id === action.payload._id) {
        state.loggedInUser = action.payload
      }
    },
    removeUser(state, action) {
      state.users = state.users.filter((user) => user._id !== action.payload)
    },
    setLoading(state, action) {
      state.isLoading = action.payload
    },
    setError(state, action) {
      state.error = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      /* ✅ טוקן */
      .addCase(validateBackendToken.fulfilled, (state, action) => {
        state.loggedInUser = action.payload
        state.isUserChecked = true
      })
      .addCase(validateBackendToken.rejected, (state) => {
        state.loggedInUser = null
        state.isUserChecked = true
      })

      /* ✅ התחברות + הרשמה */
      .addCase(login.fulfilled, (state, action) => {
        state.loggedInUser = action.payload
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loggedInUser = action.payload
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loggedInUser = action.payload
      })
      .addCase(logout.fulfilled, (state) => {
        state.loggedInUser = null
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loggedInUser = action.payload
      })
      .addCase(deleteUserAccount.fulfilled, (state) => {
        state.loggedInUser = null
      })

      /* ✅ עורכים מובילים */
      .addCase(fetchTopEditors.fulfilled, (state, action) => {
        state.topEditors = action.payload
      })

      /* ✅ משתמשים */
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false
        state.users = action.payload
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      /* ✅ משתמש לפי ID או Username */
      .addCase(fetchUserById.fulfilled, (state, action) => {
        const user = action.payload
        const idx = state.users.findIndex((u) => u._id === user._id)
        if (idx !== -1) state.users[idx] = user
        else state.users.push(user)
      })
      .addCase(fetchUserByUsername.fulfilled, (state, action) => {
        const user = action.payload
        const idx = state.users.findIndex((u) => u._id === user._id)
        if (idx !== -1) state.users[idx] = user
        else state.users.push(user)
      })
  },
})

/* ---------------------------------------------------------- */
/* -------------------- Exports ----------------------------- */
/* ---------------------------------------------------------- */

export const {
  setLoggedInUser,
  setUserChecked,
  setEmailVerified,
  setUsers,
  addUser,
  updateUser,
  removeUser,
  setLoading,
  setError,
} = userSlice.actions

export const userReducer = userSlice.reducer
